// Image caption (Figma node 487:9927 "When scroll past, unfold"): a single button
// (.image-caption__toggle) that grows from a small dot into the full caption panel (see
// molecules/image-caption.css - .is-open is the only CSS trigger, driven entirely from here).
//
// Opens and closes on its own with scrolling (2026-10-08 - replaces hover-to-open): it unfolds
// once the caption is well inside the viewport and folds away again once it has scrolled past the
// top - both while still on screen, so either change is seen. The band is the viewport minus
// VIEW_BAND (top 15%, bottom 10% - was 25%, opened too late), shared by one IntersectionObserver
// for every caption.
//
// Click/tap and keyboard focus still work as a manual override (`pinned`), which holds until the
// next scroll-driven change resets it - so the panel can be opened or closed by hand, but scrolling
// on always brings it back to the automatic behavior.
//
// Without IntersectionObserver the captions simply start open.

const VIEW_BAND = "-15% 0px -10% 0px";

interface CaptionState {
    inView: boolean;
    pinned: boolean | null;
    // Pinned open by keyboard focus (not a click), so leaving focus unpins it again.
    pinnedByFocus: boolean;
}

const states = new WeakMap<HTMLButtonElement, CaptionState>();

function render(toggle: HTMLButtonElement): void {
    const state = states.get(toggle);
    if (!state) {
        return;
    }
    const open = state.pinned ?? state.inView;
    toggle.classList.toggle("is-open", open);
    toggle.setAttribute("aria-expanded", String(open));
}

function initToggle(toggle: HTMLButtonElement, observer: IntersectionObserver | null): void {
    const state: CaptionState = { inView: observer === null, pinned: null, pinnedByFocus: false };
    states.set(toggle, state);

    // Keyboard focus only - a mouse click focuses the button first, and opening on that would let
    // the click itself immediately flip it shut again.
    toggle.addEventListener("focusin", () => {
        if (toggle.matches(":focus-visible")) {
            state.pinned = true;
            state.pinnedByFocus = true;
            render(toggle);
        }
    });

    // Only undoes a focus pin. A click pin holds until the next scroll-driven change - clearing it
    // here made it revert on the next click elsewhere in Chrome/Firefox, but not in Safari (where
    // clicking doesn't focus a button).
    toggle.addEventListener("focusout", () => {
        if (state.pinnedByFocus) {
            state.pinned = null;
            state.pinnedByFocus = false;
            render(toggle);
        }
    });

    toggle.addEventListener("click", () => {
        state.pinned = !(state.pinned ?? state.inView);
        state.pinnedByFocus = false;
        render(toggle);
    });

    render(toggle);
    observer?.observe(toggle);
}

export function initImageCaptions(): void {
    const toggles = document.querySelectorAll<HTMLButtonElement>("[data-image-caption] .image-caption__toggle");
    if (toggles.length === 0) {
        return;
    }

    const observer = "IntersectionObserver" in window
        ? new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const toggle = entry.target as HTMLButtonElement;
                const state = states.get(toggle);
                if (!state || state.inView === entry.isIntersecting) {
                    continue;
                }
                state.inView = entry.isIntersecting;
                state.pinned = null;
                state.pinnedByFocus = false;
                render(toggle);
            }
        }, { rootMargin: VIEW_BAND })
        : null;

    toggles.forEach((toggle) => initToggle(toggle, observer));
}
